import { Deferred } from '@sama/js-core';
import { isEmpty, isFunction } from '@sama/micro-dash';

/**
 * Timing can be a pain in Angular. Use this to delay execution of some code
 * without going all the way to `$timeout`. This will queue up `fn` on the
 * "microtask queue", which means it will run later, but still before the
 * javascript event thread yields. This is nice for tests, because it means
 * you don't have to add `$timeout.flush(0)`.
 */
export function delayExecution(fn: () => void) {
  return Promise.resolve().then(fn);
}

/**
 * Check if function is a promise
 */
export function isPromise(value: any): value is Promise<any> {
  return value && isFunction(value.then);
}

/**
 * Execute a chain of promises
 */
export function whenAll<T1, T2>(
  promises: [Promise<T1>, Promise<T2>],
): Promise<[T1, T2]>;
export function whenAll<T>(promises: Array<Promise<T>>): Promise<T[]>;
export function whenAll<T>(promises: Array<Promise<T>>) {
  let remaining = promises.length;
  const master = new Deferred<T[]>();
  const successes: T[] = [];
  const failures: any[] = [];

  promises.forEach((promise: Promise<T>, key: number) => {
    void promise
      .then(
        (value) => {
          successes[key] = value;
        },
        (reason) => {
          failures[key] = reason;
        },
      )
      .then(() => {
        if (--remaining === 0) {
          if (isEmpty(failures)) {
            return master.resolve(successes);
          } else {
            return master.reject({ successes, failures });
          }
        }
      });
  });

  return master.promise;
}
